import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { cn } from '@/shared/lib/utils';

type ThemeToggleProps = {
  className?: string;
};

// 스위치 치수 (px)
const W = 52;
const H = 28;
const THUMB = 22;
const PAD = 3;
const TRAVEL = W - THUMB - PAD * 2; // thumb 이동 거리

/**
 * iOS Core 라이트/다크 토글 — 단일 thumb 스위치.
 *
 * 레퍼런스(DAY/NIGHT MODE 스위치) 감성:
 * - 큰 원형 thumb 1개가 '현재 모드' 아이콘만 표시 (라이트=해 / 다크=달)
 * - 트랙 색이 모드에 따라 전환 (라이트=밝은 회색 / 다크=검정)
 * - 라이트 = thumb 우측 / 다크 = thumb 좌측 (레퍼런스와 동일)
 * - thumb·아이콘은 모드 무관 고정색(흰 thumb + 어두운 아이콘) → 항상 또렷
 */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const isDark = mounted && resolvedTheme === 'dark';
  const label = isDark ? '라이트 모드로 전환' : '다크 모드로 전환';

  return (
    <button
      type='button'
      role='switch'
      aria-checked={isDark}
      aria-label={label}
      title={label}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={cn(
        'relative inline-flex flex-shrink-0 cursor-pointer items-center rounded-full',
        className,
      )}
      style={{
        width: W,
        height: H,
        padding: PAD,
        // 트랙 색 전환 — 라이트 밝은 회색 / 다크 검정
        background: isDark ? '#000000' : '#e5e5ea',
        border: '1px solid var(--bs-line)',
        transition: 'background 300ms ease',
      }}
    >
      <span
        aria-hidden
        className='inline-flex items-center justify-center rounded-full'
        style={{
          width: THUMB,
          height: THUMB,
          background: '#ffffff',
          color: '#1c1c1e',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          // 라이트 = 우측 / 다크 = 좌측
          transform: `translateX(${isDark ? 0 : TRAVEL}px)`,
          opacity: mounted ? 1 : 0,
          transition:
            'transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease',
        }}
      >
        {isDark ? (
          <Moon size={13} strokeWidth={2} aria-hidden />
        ) : (
          <Sun size={13} strokeWidth={2} aria-hidden />
        )}
      </span>
    </button>
  );
}
